import { useState, useEffect } from 'react';
import { ArrowsClockwise, Check, X, Buildings, Storefront, ArrowRight } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getMigrationPreview, confirmMigration } from '../lib/api';
import { toast } from 'sonner';

export default function CustomerMigration() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [stats, setStats] = useState({ total: 0, unmigrated: 0 });

  const loadPreview = async () => {
    setLoading(true);
    try {
      const res = await getMigrationPreview();
      setGroups(res.data.groups);
      setStats({ total: res.data.total_groups, unmigrated: res.data.unmigrated_groups });
    } catch (err) {
      toast.error('Failed to load migration preview');
    }
    setLoading(false);
  };

  useEffect(() => { loadPreview(); }, []);

  const handleConfirm = async (group, classification, editedName) => {
    const norm = group.normalized;
    setProcessing(prev => ({ ...prev, [norm]: true }));
    try {
      const res = await confirmMigration({
        company_name: editedName || group.suggested_name,
        classification,
        variant_names: group.variants.map(v => v.name),
        country: '',
      });
      toast.success(
        classification === 'customer'
          ? `Created customer "${editedName || group.suggested_name}" (${res.data.quotes_linked} quotes, ${res.data.challans_linked} challans linked)`
          : classification === 'vendor'
          ? `Queued "${editedName || group.suggested_name}" as vendor`
          : `Skipped "${editedName || group.suggested_name}"`
      );
      setGroups(prev => prev.filter(g => g.normalized !== norm));
      setStats(prev => ({ ...prev, unmigrated: prev.unmigrated - 1 }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
    setProcessing(prev => ({ ...prev, [norm]: false }));
  };

  return (
    <div className="space-y-6" data-testid="customer-migration">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customer Migration Tool</h1>
          <p className="text-sm text-gray-500 mt-1">
            Review and classify names from existing quotes and challans.
            {stats.unmigrated > 0 && <span className="ml-1 font-medium text-amber-600">{stats.unmigrated} groups to review</span>}
          </p>
        </div>
        <Button variant="outline" onClick={loadPreview} disabled={loading}>
          <ArrowsClockwise size={16} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
        </Button>
      </div>

      <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <strong>How it works:</strong> For each name group below, classify as:
        <span className="inline-flex items-center gap-1 mx-1"><Buildings size={14} /><strong>Customer</strong></span> (creates customer record, links quotes/challans),
        <span className="inline-flex items-center gap-1 mx-1"><Storefront size={14} /><strong>Vendor</strong></span> (queued for Vendor module),
        or <span className="inline-flex items-center gap-1 mx-1"><X size={14} /><strong>Skip</strong></span>.
        Challan-only entries may be vendors/fabricators -- review carefully.
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading migration preview...</div>
      ) : groups.filter(g => !g.all_migrated).length === 0 ? (
        <div className="text-center py-12 border rounded-lg bg-green-50">
          <Check size={40} className="mx-auto mb-2 text-green-600" />
          <div className="font-semibold text-green-800">All entries have been migrated or classified!</div>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.filter(g => !g.all_migrated).map(group => (
            <MigrationGroupCard key={group.normalized} group={group}
              onConfirm={handleConfirm}
              processing={!!processing[group.normalized]} />
          ))}
        </div>
      )}
    </div>
  );
}

function MigrationGroupCard({ group, onConfirm, processing }) {
  const [editedName, setEditedName] = useState(group.suggested_name);
  const [classification, setClassification] = useState(group.variants[0]?.suggested_classification || 'customer');

  const totalQuotes = group.variants.reduce((sum, v) => sum + v.quote_count, 0);
  const totalChallans = group.variants.reduce((sum, v) => sum + v.challan_count, 0);
  const challanOnly = totalQuotes === 0 && totalChallans > 0;

  return (
    <div className={`border rounded-lg p-4 ${challanOnly ? 'border-amber-200 bg-amber-50/30' : 'bg-white'}`}
      data-testid={`migration-group-${group.normalized}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Input value={editedName} onChange={e => setEditedName(e.target.value)}
              className="font-semibold text-base max-w-xs" disabled={processing} />
            {challanOnly && <Badge className="bg-amber-100 text-amber-800 text-xs">Challan only</Badge>}
          </div>
          <div className="text-sm text-gray-500 space-y-1">
            {group.variants.length > 1 && (
              <div>Variants: {group.variants.map(v => v.name).join(', ')}</div>
            )}
            <div className="flex gap-4">
              <span>{totalQuotes} quote{totalQuotes !== 1 ? 's' : ''}</span>
              <span>{totalChallans} challan{totalChallans !== 1 ? 's' : ''}</span>
              {group.variants.some(v => v.challan_types.length > 0) && (
                <span className="text-amber-600">
                  Types: {[...new Set(group.variants.flatMap(v => v.challan_types))].filter(Boolean).join(', ') || 'unset'}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Select value={classification} onValueChange={setClassification} disabled={processing}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="customer"><div className="flex items-center gap-1"><Buildings size={14} />Customer</div></SelectItem>
              <SelectItem value="vendor"><div className="flex items-center gap-1"><Storefront size={14} />Vendor</div></SelectItem>
              <SelectItem value="skip"><div className="flex items-center gap-1"><X size={14} />Skip</div></SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => onConfirm(group, classification, editedName)} disabled={processing} size="sm"
            data-testid={`confirm-${group.normalized}`}>
            {processing ? '...' : <><ArrowRight size={14} className="mr-1" />Confirm</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
